import { useCallback, useEffect, useRef, useState } from 'react';

import Icon from './Icon.jsx';
import RainField from './RainField.jsx';
import { streamChat, ChatError, CHAT_SUGGESTIONS } from '../lib/chatClient.js';

/**
 * Scripture AI chat.
 *
 * Streams from our Express proxy (see server/index.js) token by token. The
 * verse currently being studied rides along as context so "explain this verse"
 * resolves to the right one.
 */
export default function ChatScreen({
  verse,
  onClearVerse,
  isPremium,
  signedIn,
  remaining,
  onConsume,
  onUpgrade,
  onNeedAccount,
}) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Keep the newest message in view as tokens land.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  // Abort any in-flight request if the user navigates away.
  useEffect(() => () => abortRef.current?.abort(), []);

  /**
   * Run one assistant turn against an explicit history.
   *
   * Taking the history as an argument rather than reading `messages` from the
   * closure is what makes retry correct: on failure the user's turn stays in
   * `messages`, so a retry re-runs the same history instead of appending a
   * duplicate copy of the question.
   */
  const runTurn = useCallback(
    async (history) => {
      setError(null);
      setMessages([...history, { role: 'assistant', content: '', pending: true }]);
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await streamChat({
          messages: history,
          verse,
          signal: controller.signal,
          onDelta: (chunk) => {
            setMessages((current) => {
              const next = [...current];
              const last = next[next.length - 1];
              if (last?.role === 'assistant') {
                next[next.length - 1] = {
                  ...last,
                  content: last.content + chunk,
                  pending: false,
                };
              }
              return next;
            });
          },
        });
      } catch (err) {
        if (err?.name === 'AbortError') {
          // Stopped on purpose — keep whatever streamed in.
          setMessages((current) =>
            current.map((m, i) =>
              i === current.length - 1 ? { ...m, pending: false, stopped: true } : m,
            ),
          );
        } else {
          const chatError =
            err instanceof ChatError
              ? err
              : new ChatError('Something went wrong. Please try again.');
          setError({ message: chatError.message, retryable: chatError.retryable });
          // Drop the empty assistant bubble; the error card replaces it. The
          // user's turn stays, so retry can replay this exact history.
          setMessages((current) => {
            const last = current[current.length - 1];
            return last?.role === 'assistant' && !last.content ? current.slice(0, -1) : current;
          });
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
        setMessages((current) =>
          current.map((m, i) => (i === current.length - 1 ? { ...m, pending: false } : m)),
        );
      }
    },
    [verse],
  );

  const send = useCallback(
    (text) => {
      const content = text.trim();
      if (!content || streaming) return;

      // The server meters this per account, so it will refuse an anonymous
      // caller. Ask here rather than letting someone type a question, press
      // send, and be handed a 401 for their trouble.
      if (!signedIn) {
        onNeedAccount();
        return;
      }

      if (!isPremium && !onConsume()) {
        onUpgrade();
        return;
      }

      setDraft('');
      runTurn([...messages, { role: 'user', content }]);
    },
    [messages, streaming, signedIn, isPremium, onConsume, onNeedAccount, onUpgrade, runTurn],
  );

  const stop = useCallback(() => abortRef.current?.abort(), []);

  // Replay the same history. Doesn't spend another message from the free
  // allowance — the first attempt never produced an answer.
  const retry = useCallback(() => {
    if (streaming || messages.at(-1)?.role !== 'user') return;
    runTurn(messages);
  }, [streaming, messages, runTurn]);

  const empty = messages.length === 0;

  return (
    <section className="screen screen--chat">
      {/* Rain on the window behind the page. The room this screen is trying to
          be is a study on a wet evening, and weather is most of what makes a
          room feel like somewhere rather than something. */}
      <RainField />

      <div className="screen__heading">
        <h2>Scripture AI</h2>
        <p>Ask about context, meaning or how a passage connects to the rest of the Bible.</p>
      </div>

      {verse ? (
        <div className="context-chip">
          <span className="context-chip__label">Studying</span>
          <span className="context-chip__ref">{verse.ref}</span>
          <button
            type="button"
            className="context-chip__clear"
            onClick={onClearVerse}
            aria-label="Remove verse context"
          >
            <Icon name="close" size={12} />
          </button>
        </div>
      ) : null}

      <div className="chat" ref={scrollRef}>
        {empty ? (
          <div className="chat__intro">
            <p className="chat__intro-icon">
              <Icon name="dove" size={38} />
            </p>
            <h3>What would you like to understand?</h3>
            <div className="suggestions">
              {CHAT_SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="suggestion"
                  onClick={() => send(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((message, i) => (
            <div key={i} className={`bubble bubble--${message.role}`}>
              {/* The reply is set like a page of scripture rather than a chat
                  bubble: a raised capital, a serif face, a ruled margin. It is
                  the same words either way — but a bubble says "notification"
                  and a set page says "read this". */}
              {message.role === 'assistant' && message.content ? (
                <>
                  <span className="bubble__initial" aria-hidden="true">
                    {message.content.trimStart().charAt(0)}
                  </span>
                  {message.content.trimStart().slice(1)}
                </>
              ) : (
                message.content
              )}
              {message.pending ? (
                <span className="typing" aria-label="Thinking">
                  <i />
                  <i />
                  <i />
                </span>
              ) : null}
              {message.stopped ? <span className="bubble__note">stopped</span> : null}
            </div>
          ))
        )}

        {error ? (
          <div className="chat-error" role="alert">
            <strong>Couldn’t get a reply</strong>
            <p>{error.message}</p>
            {error.retryable ? (
              <button type="button" className="btn btn--sm btn--muted" onClick={retry}>
                Try again
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          send(draft);
        }}
      >
        <textarea
          ref={inputRef}
          className="composer__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={verse ? `Ask about ${verse.ref}…` : 'Ask anything about the Bible…'}
          rows={1}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter is a newline.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(draft);
            }
          }}
        />
        {streaming ? (
          <button type="button" className="btn btn--muted composer__btn" onClick={stop}>
            Stop
          </button>
        ) : (
          <button
            type="submit"
            className="btn btn--primary composer__btn"
            disabled={!draft.trim()}
          >
            Send
          </button>
        )}
      </form>

      {!signedIn ? (
        <p className="chat__quota">
          Free questions come with an account ·{' '}
          <button type="button" className="linkish" onClick={onNeedAccount}>
            create one
          </button>
        </p>
      ) : !isPremium ? (
        <p className="chat__quota">
          {remaining > 0 ? (
            <>
              {remaining} free question{remaining === 1 ? '' : 's'} left today ·{' '}
              <button type="button" className="linkish" onClick={onUpgrade}>
                go unlimited
              </button>
            </>
          ) : (
            <>
              You’ve used today’s free questions ·{' '}
              <button type="button" className="linkish" onClick={onUpgrade}>
                unlock unlimited
              </button>
            </>
          )}
        </p>
      ) : null}
    </section>
  );
}
